/**
 * Invites Service
 *
 * Service layer for interacting with the Invites state.
 * Manages real-time Firestore subscription for user's invites document.
 * Automatically subscribes/unsubscribes based on auth state changes.
 */

import { Injectable, inject, OnDestroy } from '@angular/core';
import { Store } from '@ngxs/store';
import { Subscription } from 'rxjs';
import { InvitesState } from './invites.state';
import {
  InitializeInvites,
  InvitesLoaded,
  InvitesLoadFailure,
  ClearInvites,
} from './invites.actions';
import { AuthState } from 'src/app/store/auth';
import { FirestoreService } from 'src/app/services/firestore/firestore';
import { Invite } from 'src/app/models/schema';
import { Unsubscribe } from '@angular/fire/firestore';

/**
 * Invites Service
 *
 * Handles real-time synchronization of user invites from Firestore.
 * Subscribes to invites/{email} document and updates state automatically.
 */
@Injectable({
  providedIn: 'root',
})
export class InvitesService implements OnDestroy {
  private store: Store = inject(Store);
  private firestoreService: FirestoreService = inject(FirestoreService);

  /**
   * Active Firestore listener unsubscribe function
   */
  private unsubscribe?: Unsubscribe;

  /**
   * Subscription to auth state changes
   */
  private authSubscription?: Subscription;

  /**
   * Currently subscribed email address
   */
  private currentEmail: string | null = null;

  /**
   * Observable of the entire invites state
   */
  readonly invitesState$ = this.store.select(InvitesState.getState);

  /**
   * Observable of the invites array
   */
  readonly invites$ = this.store.select(InvitesState.invites);

  /**
   * Observable of pending invites
   */
  readonly pendingInvites$ = this.store.select(InvitesState.pendingInvites);

  /**
   * Observable of pending invites count
   */
  readonly pendingCount$ = this.store.select(InvitesState.pendingCount);

  /**
   * Observable of invites count
   */
  readonly count$ = this.store.select(InvitesState.count);

  /**
   * Observable of loading state
   */
  readonly loading$ = this.store.select(InvitesState.loading);

  /**
   * Observable of loaded state
   */
  readonly loaded$ = this.store.select(InvitesState.loaded);

  /**
   * Initialize the service and start listening to auth changes
   */
  initialize(): void {
    // Subscribe to auth state changes
    this.authSubscription = this.store
      .select(AuthState.userEmail)
      .subscribe((email: string | null) => {
        if (email && email !== this.currentEmail) {
          // User logged in or email changed - subscribe to their invites
          this.subscribeToInvites(email);
        } else if (!email && this.currentEmail) {
          // User logged out - clear invites and unsubscribe
          this.unsubscribeFromInvites();
          this.store.dispatch(new ClearInvites());
        }
      });
  }

  /**
   * Subscribe to Firestore invites document for a specific email
   *
   * @param email - User's email address
   */
  private subscribeToInvites(email: string): void {
    // Unsubscribe from previous listener if any
    if (this.unsubscribe) {
      this.unsubscribe();
    }

    const normalizedEmail = email.toLowerCase().trim();
    this.currentEmail = normalizedEmail;

    // Dispatch initialize action
    this.store.dispatch(new InitializeInvites(normalizedEmail));

    const inviteDocPath = `invites/${normalizedEmail}`;

    try {
      // Subscribe to real-time updates
      this.unsubscribe = this.firestoreService.onDocumentChange<{
        email: string;
        partnerId?: string;
        isInitialTeamAdmin?: boolean;
        invitesList: Invite[];
      }>(
        inviteDocPath,
        (doc: { email: string; partnerId?: string; isInitialTeamAdmin?: boolean; invitesList: Invite[] } | null) => {
          // Document updated or not found
          if (doc === null) {
            // Document doesn't exist yet - user has no invites
            this.store.dispatch(new InvitesLoaded([]));
          } else {
            // Document exists - load invites
            const invites = doc.invitesList || [];
            this.store.dispatch(new InvitesLoaded(invites));
          }
        }
      );
    } catch (error: any) {
      console.error('[InvitesService] Error subscribing to invites:', error);
      this.store.dispatch(new InvitesLoadFailure(error.message || 'Failed to subscribe to invites'));
    }
  }

  /**
   * Unsubscribe from current Firestore listener
   */
  private unsubscribeFromInvites(): void {
    if (this.unsubscribe) {
      this.unsubscribe();
      this.unsubscribe = undefined;
    }
    this.currentEmail = null;
  }

  /**
   * Get invites synchronously from state
   */
  getInvites(): Invite[] {
    return this.store.selectSnapshot(InvitesState.invites);
  }

  /**
   * Get pending invites synchronously from state
   */
  getPendingInvites(): Invite[] {
    return this.store.selectSnapshot(InvitesState.pendingInvites);
  }

  /**
   * Get pending invites count synchronously from state
   */
  getPendingCount(): number {
    return this.store.selectSnapshot(InvitesState.pendingCount);
  }

  /**
   * Cleanup on service destruction
   */
  ngOnDestroy(): void {
    this.unsubscribeFromInvites();
    if (this.authSubscription) {
      this.authSubscription.unsubscribe();
    }
  }
}

